import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

const BG = '#05060f';
const BORDER = '#1a2040';
const TEAL = '#2DC896';
const WHITE = '#FFFFFF';
const ORBITRON = "'Orbitron', monospace";
const MONO = "'JetBrains Mono', 'Courier New', monospace";

const HeroVideo = ({ src, sectionTitle, sectionSubtitle }) => {
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setPlaying(true);
    }
  };

  return (
    <section className="py-36 px-6" style={{ borderBottom: `1px solid ${BORDER}` }}>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 style={{ fontFamily: ORBITRON, fontWeight: 700, fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', color: WHITE, marginBottom: '1rem' }}>
            {sectionTitle}
          </h2>
          {sectionSubtitle && (
            <p style={{ color: TEAL, fontFamily: MONO, fontSize: '0.9rem', letterSpacing: '0.06em' }}>
              {sectionSubtitle}
            </p>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          style={{
            position: 'relative',
            width: '100%',
            paddingBottom: '56.25%',
            background: BG,
            border: `1px solid ${BORDER}`,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: `0 0 40px rgba(45, 200, 150, 0.12), 0 0 80px rgba(45, 200, 150, 0.06)`,
          }}
        >
          <video
            ref={videoRef}
            src={src}
            controls={playing}
            preload="metadata"
            playsInline
            onEnded={() => setPlaying(false)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />

          {!playing && (
            <button
              onClick={handlePlay}
              aria-label="Play video"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(180deg, rgba(5,6,15,0.25) 0%, rgba(5,6,15,0.55) 100%)',
                border: 'none',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              <span
                style={{
                  position: 'relative',
                  width: '88px',
                  height: '88px',
                  borderRadius: '50%',
                  background: 'rgba(5, 6, 15, 0.75)',
                  border: `2px solid ${TEAL}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backdropFilter: 'blur(8px)',
                  boxShadow: `0 0 30px rgba(45, 200, 150, 0.4)`,
                }}
              >
                <span
                  style={{
                    position: 'absolute',
                    top: '-8px',
                    left: '-8px',
                    right: '-8px',
                    bottom: '-8px',
                    borderRadius: '50%',
                    border: `2px solid ${TEAL}`,
                    opacity: 0.5,
                    animation: 'recallcorePulse 2s ease-out infinite',
                  }}
                />
                <span
                  style={{
                    width: 0,
                    height: 0,
                    borderTop: '14px solid transparent',
                    borderBottom: '14px solid transparent',
                    borderLeft: `22px solid ${TEAL}`,
                    marginLeft: '6px',
                  }}
                />
              </span>
            </button>
          )}

          <style>{`
            @keyframes recallcorePulse {
              0% { transform: scale(1); opacity: 0.5; }
              100% { transform: scale(1.6); opacity: 0; }
            }
          `}</style>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroVideo;
